import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const PatientPortalPage = () => {
  const [activeForm, setActiveForm] = useState<'new' | 'existing' | null>(null);
  const [newPatientData, setNewPatientData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    insuranceProvider: '',
    policyNumber: '',
    reasonForVisit: '',
    informedConsent: false,
    telepsychiatryConsent: false,
    hipaaConsent: false
  });

  const [existingPatientData, setExistingPatientData] = useState({
    patientId: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    requestType: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleNewPatientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!newPatientData.informedConsent || !newPatientData.hipaaConsent) {
      setSubmitStatus({ type: 'error', message: 'Please accept all required consent forms' });
      return;
    }

    try {
      const formData = new URLSearchParams();
      Object.entries(newPatientData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d58v9ufahj0hkf71i8g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Registration submitted successfully! Our team will contact you within 24 hours.' });
        setNewPatientData({
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          email: '',
          phone: '',
          address: '',
          emergencyContact: '',
          emergencyPhone: '',
          insuranceProvider: '',
          policyNumber: '',
          reasonForVisit: '',
          informedConsent: false,
          telepsychiatryConsent: false,
          hipaaConsent: false
        });
      } else {
        setSubmitStatus({ type: 'error', message: 'Submission failed. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  const handleExistingPatientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const formData = new URLSearchParams();
      Object.entries(existingPatientData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d58v9ufahj0hkf71i8gg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Request submitted successfully! We will respond within 24 hours.' });
        setExistingPatientData({
          patientId: '',
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          email: '',
          phone: '',
          requestType: '',
          message: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: 'Submission failed. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Patient Portal
          </motion.h1>
          <motion.p
            className="text-white/80 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> Patient Portal
          </motion.p>
        </div>
      </section>

      {/* Portal Options */}
      {!activeForm && (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Portal Access</h2>
              <p className="text-lg text-gray-600">Select the appropriate option based on your patient status</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div
                onClick={() => setActiveForm('new')}
                className="bg-white rounded-xl p-10 shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-[rgba(75,162,75)] cursor-pointer group"
              >
                <div className="w-20 h-20 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6 group-hover:bg-[rgba(75,162,75)] transition-all">
                  <i className="ri-user-add-line text-4xl text-[rgba(75,162,75)] group-hover:text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">New Patient Registration</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  First time visiting Solutions Healthcare? Complete your registration and consent forms here.
                </p>
                <div className="flex items-center text-[rgba(75,162,75)] font-semibold group-hover:text-[rgba(75,162,75,0.8)]">
                  Get Started
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>

              <a
                href="/payment-setup"
                className="group bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-8 border border-gray-100 hover:border-[rgba(75,162,75)] cursor-pointer"
              >
                <div className="w-16 h-16 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6 group-hover:bg-[rgba(75,162,75)] transition-colors">
                  <i className="ri-secure-payment-line text-3xl text-[rgba(75,162,75)] group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Payment Setup</h3>
                <p className="text-gray-600 mb-4">
                  Set up your payment information for appointments and services
                </p>
                <div className="flex items-center text-[rgba(75,162,75)] font-medium group-hover:gap-3 transition-all">
                  <span>Set Up Payment</span>
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </div>
              </a>

              <div
                onClick={() => setActiveForm('existing')}
                className="bg-white rounded-xl p-10 shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-[rgba(75,162,75)] cursor-pointer group"
              >
                <div className="w-20 h-20 bg-[rgba(75,162,75,0.1)] rounded-full flex items-center justify-center mb-6 group-hover:bg-[rgba(75,162,75)] transition-all">
                  <i className="ri-user-line text-4xl text-[rgba(75,162,75)] group-hover:text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Existing Patient Access</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Already a patient? Access your documents, update information, or contact our office.
                </p>
                <div className="flex items-center text-[rgba(75,162,75)] font-semibold group-hover:text-[rgba(75,162,75,0.8)]">
                  Access Portal
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            </div>

            <div className="mt-12 bg-[rgba(75,162,75,0.05)] rounded-xl p-8 border border-[rgba(75,162,75,0.2)]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[rgba(75,162,75)] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Your Privacy is Protected</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All information submitted through our patient portal is encrypted and HIPAA-compliant. Your personal health information is secure and confidential.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* New Patient Form */}
      {activeForm === 'new' && (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => {
                setActiveForm(null);
                setSubmitStatus(null);
              }}
              className="mb-8 text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Portal Options
            </button>

            <div className="bg-white rounded-xl shadow-lg p-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">New Patient Registration</h2>
              <p className="text-gray-600 mb-8">Please complete all required fields to begin your care with Solutions Healthcare LLC.</p>

              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <i className={`${submitStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                    <span>{submitStatus.message}</span>
                  </div>
                </div>
              )}

              <form id="new-patient-form" data-readdy-form onSubmit={handleNewPatientSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={newPatientData.firstName}
                      onChange={(e) => setNewPatientData({ ...newPatientData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={newPatientData.lastName}
                      onChange={(e) => setNewPatientData({ ...newPatientData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      required
                      value={newPatientData.dateOfBirth}
                      onChange={(e) => setNewPatientData({ ...newPatientData, dateOfBirth: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={newPatientData.phone}
                      onChange={(e) => setNewPatientData({ ...newPatientData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={newPatientData.email}
                    onChange={(e) => setNewPatientData({ ...newPatientData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Home Address *</label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={newPatientData.address}
                    onChange={(e) => setNewPatientData({ ...newPatientData, address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Emergency Contact Name *</label>
                    <input
                      type="text"
                      name="emergencyContact"
                      required
                      value={newPatientData.emergencyContact}
                      onChange={(e) => setNewPatientData({ ...newPatientData, emergencyContact: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Emergency Contact Phone *</label>
                    <input
                      type="tel"
                      name="emergencyPhone"
                      required
                      value={newPatientData.emergencyPhone}
                      onChange={(e) => setNewPatientData({ ...newPatientData, emergencyPhone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Insurance Provider</label>
                    <select
                      name="insuranceProvider"
                      value={newPatientData.insuranceProvider}
                      onChange={(e) => setNewPatientData({ ...newPatientData, insuranceProvider: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    >
                      <option value="">Select Insurance</option>
                      <option value="Optum">Optum</option>
                      <option value="Aetna">Aetna</option>
                      <option value="Cigna">Cigna</option>
                      <option value="Medicaid">Medicaid</option>
                      <option value="Medicare">Medicare</option>
                      <option value="Colorado Access">Colorado Access</option>
                      <option value="Self-Pay">Self-Pay</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Policy Number</label>
                    <input
                      type="text"
                      name="policyNumber"
                      value={newPatientData.policyNumber}
                      onChange={(e) => setNewPatientData({ ...newPatientData, policyNumber: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Visit *</label>
                  <textarea
                    name="reasonForVisit"
                    required
                    rows={4}
                    maxLength={500}
                    value={newPatientData.reasonForVisit}
                    onChange={(e) => setNewPatientData({ ...newPatientData, reasonForVisit: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="Please briefly describe your mental health concerns and what you hope to address in treatment (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{newPatientData.reasonForVisit.length}/500 characters</p>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Required Consent Forms</h3>
                  
                  <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="informedConsent"
                        required
                        checked={newPatientData.informedConsent}
                        onChange={(e) => setNewPatientData({ ...newPatientData, informedConsent: e.target.checked })}
                        className="mt-1 w-5 h-5 text-[rgba(75,162,75)] border-gray-300 rounded focus:ring-[rgba(75,162,75)]"
                      />
                      <span className="text-sm text-gray-700">
                        <strong>Informed Consent for Treatment *</strong> - I consent to psychiatric evaluation and treatment at Solutions Healthcare LLC. I understand the nature of psychiatric services, potential risks and benefits, and my right to refuse or discontinue treatment at any time.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="telepsychiatryConsent"
                        checked={newPatientData.telepsychiatryConsent}
                        onChange={(e) => setNewPatientData({ ...newPatientData, telepsychiatryConsent: e.target.checked })}
                        className="mt-1 w-5 h-5 text-[rgba(75,162,75)] border-gray-300 rounded focus:ring-[rgba(75,162,75)]"
                      />
                      <span className="text-sm text-gray-700">
                        <strong>Telepsychiatry Consent</strong> - I consent to receive mental health services via secure video conferencing. I understand the benefits and limitations of telehealth services.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="hipaaConsent"
                        required
                        checked={newPatientData.hipaaConsent}
                        onChange={(e) => setNewPatientData({ ...newPatientData, hipaaConsent: e.target.checked })}
                        className="mt-1 w-5 h-5 text-[rgba(75,162,75)] border-gray-300 rounded focus:ring-[rgba(75,162,75)]"
                      />
                      <span className="text-sm text-gray-700">
                        <strong>HIPAA Privacy Notice *</strong> - I acknowledge receipt of the Notice of Privacy Practices and understand how my health information may be used and disclosed.
                      </span>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[rgba(75,162,75)] text-white py-4 rounded-lg font-semibold hover:bg-[rgba(75,162,75,0.9)] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Submit Registration
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Existing Patient Form */}
      {activeForm === 'existing' && (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => {
                setActiveForm(null);
                setSubmitStatus(null);
              }}
              className="mb-8 text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Portal Options
            </button>

            <div className="bg-white rounded-xl shadow-lg p-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Existing Patient Portal</h2>
              <p className="text-gray-600 mb-8">Access your treatment documents or contact our office with questions.</p>

              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <i className={`${submitStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                    <span>{submitStatus.message}</span>
                  </div>
                </div>
              )}

              <form id="existing-patient-form" data-readdy-form onSubmit={handleExistingPatientSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Patient ID *</label>
                  <input
                    type="text"
                    name="patientId"
                    required
                    value={existingPatientData.patientId}
                    onChange={(e) => setExistingPatientData({ ...existingPatientData, patientId: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    placeholder="Your patient ID number"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={existingPatientData.firstName}
                      onChange={(e) => setExistingPatientData({ ...existingPatientData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={existingPatientData.lastName}
                      onChange={(e) => setExistingPatientData({ ...existingPatientData, lastName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth *</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    required
                    value={existingPatientData.dateOfBirth}
                    onChange={(e) => setExistingPatientData({ ...existingPatientData, dateOfBirth: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={existingPatientData.email}
                      onChange={(e) => setExistingPatientData({ ...existingPatientData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={existingPatientData.phone}
                      onChange={(e) => setExistingPatientData({ ...existingPatientData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Request Type *</label>
                  <select
                    name="requestType"
                    required
                    value={existingPatientData.requestType}
                    onChange={(e) => setExistingPatientData({ ...existingPatientData, requestType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent"
                  >
                    <option value="">Select Request Type</option>
                    <option value="Document Access">Access Treatment Documents</option>
                    <option value="Prescription Refill">Prescription Refill Request</option>
                    <option value="Appointment Change">Change/Cancel Appointment</option>
                    <option value="Billing Question">Billing Question</option>
                    <option value="Medical Records">Medical Records Request</option>
                    <option value="General Question">General Question</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={existingPatientData.message}
                    onChange={(e) => setExistingPatientData({ ...existingPatientData, message: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgba(75,162,75)] focus:border-transparent resize-none"
                    placeholder="Please provide details about your request (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">{existingPatientData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[rgba(75,162,75)] text-white py-4 rounded-lg font-semibold hover:bg-[rgba(75,162,75,0.9)] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Submit Request
                </button>
              </form>
            </div>

            <div className="mt-8 bg-[rgba(75,162,75,0.05)] rounded-xl p-6 border border-[rgba(75,162,75,0.2)]">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Need Immediate Assistance?</h3>
              <div className="flex flex-wrap gap-4">
                <a href="tel:+13035551234" className="text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold cursor-pointer">
                  <i className="ri-phone-line mr-2"></i>
                  (303) 555-1234
                </a>
                <a href="mailto:info@solutionshealthcare.com" className="text-[rgba(75,162,75)] hover:text-[rgba(75,162,75,0.8)] font-semibold cursor-pointer">
                  <i className="ri-mail-line mr-2"></i>
                  info@solutionshealthcare.com
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default PatientPortalPage;
